/**
 * DocsSliderProvider — Component 1 of the help-docs-panel design (Wave 2 · task 2.1).
 *
 * The single React context that owns:
 *
 *  1. `docsOpen`     — whether the Docs_Slider drawer is visible
 *  2. `chatbotOpen`  — whether the Chatbot_Dock dialog is visible
 *  3. `docContext`   — the current {@link DocsSliderContext} (route,
 *                      `errorId`, `docPath`, `anchor`)
 *
 * ## Portal roots
 *
 * On mount the provider ensures a `<div id="help-docs-portal-root">` exists
 * in `document.body`, creating it if missing. `DocsSliderContent` and
 * `ChatbotDock` are rendered via `createPortal` into that root so they
 * sit outside the app's normal DOM layout (matching the design's z-index
 * ladder: docs slider 45, chatbot dock 46).
 *
 * `ChatbotFAB` is NOT wrapped in this portal — it manages its own portal
 * into `#chatbot-launcher-slot` via {@link resolvePortalTarget} so it can
 * land in the reserved slot owned by `dashboard-redesign`.
 *
 * ## Persistence contract (Req 1.5, 1.6)
 *
 * - When `sessionKey` is defined the provider reads
 *   `readPersistedState(sessionKey)` on mount and hydrates `docsOpen` and
 *   `docContext.docPath` from the result.
 * - When `docsOpen` OR `docContext.docPath` changes, the provider writes
 *   `{ open, lastDocPath }` back through `writePersistedState`.
 * - `chatbotOpen` is NEVER persisted — Chatbot_Session always starts
 *   closed on mount (per Req 1.5 and the design's state machine).
 *
 * ## Event bridge (Req 1.7, 3.4, 8.6)
 *
 * Subscribes to `window` for the `help-docs-panel:open` CustomEvent
 * dispatched by {@link openDocsAt}. On receipt the slider opens with the
 * event's `{ path, anchor }` seeded into `docContext`.
 *
 * ## Keyboard shortcuts (Req 1.1, 1.3)
 *
 * Global `keydown` listener on `window`:
 *
 *  - `?` (unmodified)                          → toggle slider
 *  - `Ctrl+/` / `⌘/` (Meta+`/`)                → toggle slider
 *  - `Escape` (when slider is open)            → close slider
 *
 * Listeners are ignored while the user is typing inside a text input,
 * `textarea`, or `contenteditable` region so the `?` key still types
 * verbatim inside a doc note.
 *
 * ## Cleanup
 *
 * On unmount the provider:
 *
 *  - removes both `window` event listeners,
 *  - removes the `#help-docs-portal-root` element **only** if the provider
 *    created it (never touches a pre-existing root owned by another
 *    layer of the app).
 *
 * @see .kiro/specs/help-docs-panel/design.md — § Component 1
 * @see .kiro/specs/help-docs-panel/requirements.md — Requirement 1
 * @see ./DocsSliderContent.tsx
 * @see ./ChatbotFAB.tsx
 * @see ./ChatbotDock.tsx
 * @see ./openDocsAt.ts — the reverse operation (imperative open → event)
 *
 * @packageDocumentation
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

import { ChatbotDock } from "./ChatbotDock";
import { ChatbotFAB } from "./ChatbotFAB";
import { DocsSliderContent } from "./DocsSliderContent";
import { parseDeepLink } from "./parseDeepLink";
import { readPersistedState, writePersistedState } from "./persist";
import type { ChatbotTransport } from "./StubChatbotTransport";
import type { DocsManifest, DocsSliderContext } from "./types";

// ---------------------------------------------------------------------------
// Public props and context shape
// ---------------------------------------------------------------------------

/**
 * Props accepted by {@link DocsSliderProvider}.
 */
export interface DocsSliderProviderProps {
  /**
   * Hashed session identifier — the first 16 lowercase hex chars of
   * `SHA-256(ownerId)`. When undefined (e.g. a logged-out shell) the
   * provider skips persistence entirely.
   */
  sessionKey?: string;

  /**
   * The chatbot transport implementation. `StubChatbotTransport` today;
   * the real MCP client from the deferred `chatbot-mcp` spec later.
   */
  transport: ChatbotTransport;

  /**
   * The current app route (`useLocation().pathname` at the shell level),
   * e.g. `"/statements/abc-123"`. Threaded down to `DocsSliderContent`
   * so the initial-doc resolver can call `routeToDefaultDoc`.
   */
  currentRoute: string;

  /**
   * The build-time docs manifest, loaded once by the app shell from
   * `/docs/docs.manifest.json`.
   */
  manifest: DocsManifest;

  /** React children rendered inside the context provider. */
  children: ReactNode;
}

/**
 * The value exposed via {@link useDocsSlider}.
 */
export interface DocsSliderContextValue {
  /** Whether the Docs_Slider drawer is currently visible. */
  readonly docsOpen: boolean;

  /** Whether the Chatbot_Dock dialog is currently visible. */
  readonly chatbotOpen: boolean;

  /**
   * The active {@link DocsSliderContext} — route, optional `errorId`,
   * optional `docPath`, and optional `anchor`. Callers should treat it
   * as read-only.
   */
  readonly docContext: DocsSliderContext;

  /**
   * The wired {@link ChatbotTransport} implementation. Exposed so
   * `useChatbotClient()` (task 2.9) can invoke `transport.send(...)`
   * without a second injection path — the provider is the single seam
   * between the app shell's transport choice and any public bridge.
   *
   * @remarks Consumers MUST only pass `{ sessionId, message, context }`
   * to `transport.send` — never `ownerId`, never `authorization`
   * (Constitution Rule II, property test P5).
   */
  readonly transport: ChatbotTransport;

  /**
   * The hashed session identifier already prepared for the transport.
   *
   * @remarks
   * Equals the `sessionKey` prop when defined, otherwise the empty
   * string. Callers pass this directly as `ChatbotSendInput.sessionId`
   * — the transport never sees the raw `ownerId` (Constitution Rule II).
   */
  readonly sessionId: string;

  /**
   * Open the docs slider. Optionally seeds the doc path and anchor.
   * Called by consumers (e.g. `chatbotClient.openWithContext` in task 2.9,
   * or a "?" keyboard shortcut).
   */
  openDocs(path?: string, anchor?: string): void;

  /** Close the docs slider. Idempotent. */
  closeDocs(): void;

  /** Toggle the docs slider open ↔ closed. */
  toggleDocs(): void;

  /**
   * Open the chatbot dock, optionally merging a partial
   * {@link DocsSliderContext} into the current context.
   */
  openChatbot(ctx?: Partial<DocsSliderContext>): void;

  /** Close the chatbot dock. Idempotent. */
  closeChatbot(): void;

  /** Toggle the chatbot dock open ↔ closed. */
  toggleChatbot(): void;
}

// ---------------------------------------------------------------------------
// Context object (exposed for advanced composition; the hook is the API)
// ---------------------------------------------------------------------------

/**
 * The React context object. Exported for advanced consumers that need
 * to compose their own provider (e.g. Playwright wrappers or Storybook
 * stories). Most callers should use {@link useDocsSlider} instead.
 */
export const DocsSliderContextObject =
  createContext<DocsSliderContextValue | null>(null);

/**
 * Access the {@link DocsSliderContextValue} from within a
 * {@link DocsSliderProvider}.
 *
 * @throws {Error} when called outside a `DocsSliderProvider`.
 */
export function useDocsSlider(): DocsSliderContextValue {
  const value = useContext(DocsSliderContextObject);
  if (value === null) {
    throw new Error(
      "useDocsSlider must be called inside a <DocsSliderProvider>.",
    );
  }
  return value;
}

// ---------------------------------------------------------------------------
// Internal constants
// ---------------------------------------------------------------------------

/** The id of the shell-level portal root created (or reused) by the provider. */
const PORTAL_ROOT_ID = "help-docs-portal-root";

/** Data attribute stamped on portal roots we create ourselves. */
const SELF_MOUNTED_ATTR = "data-self-mounted";

/** The event type dispatched by `openDocsAt`. */
const OPEN_EVENT_TYPE = "help-docs-panel:open";

/** Slug pattern accepted for anchors — mirrors `openDocsAt.ts`. */
const ANCHOR_PATTERN = /^[a-z0-9-]+$/;

/** Fallback doc path when a deep-link fails to resolve in the manifest. */
const FALLBACK_DOC_PATH = "README.md";

// ---------------------------------------------------------------------------
// Initial-state resolver — deep-link (Req 1.7, 1.8) > persisted state (Req 1.5)
// ---------------------------------------------------------------------------

/**
 * Seed value returned by {@link resolveInitialState}.
 *
 * @internal
 */
interface InitialState {
  readonly docsOpen: boolean;
  readonly docContext: DocsSliderContext;
}

/**
 * Compute the seed `{ docsOpen, docContext }` used to initialise state on
 * mount.
 *
 * Evaluation order (task 3.3, Req 1.7 + 1.8):
 *
 *  1. Parse `window.location.search` + `window.location.hash` via
 *     {@link parseDeepLink}. If a valid `docPath` is returned:
 *      - If the path resolves in `manifest.tree` → open on that path,
 *        seeding `docContext.anchor` when provided.
 *      - If the path does NOT resolve → open on `README.md` and emit a
 *        dev-only `console.warn` (guarded by
 *        `process.env.NODE_ENV !== "production"`; production builds are
 *        silent).
 *  2. Otherwise fall back to persisted state via {@link readPersistedState}
 *     (Req 1.5). When persistence is empty or `sessionKey` is undefined,
 *     the slider starts closed with `docPath: undefined` (existing default).
 *
 * SSR-safe: when `window` is undefined the deep-link branch short-circuits
 * and we drop straight to persisted state.
 *
 * @internal
 */
function resolveInitialState(
  sessionKey: string | undefined,
  manifest: DocsManifest,
): InitialState {
  // Step 1 — deep-link takes precedence.
  if (typeof window !== "undefined") {
    const deepLink = parseDeepLink(
      window.location.search,
      window.location.hash,
    );
    if (deepLink.docPath !== undefined) {
      const resolves = manifest.tree.some(
        (entry) => entry.path === deepLink.docPath,
      );
      if (resolves) {
        return {
          docsOpen: true,
          docContext:
            deepLink.anchor === undefined
              ? { docPath: deepLink.docPath }
              : { docPath: deepLink.docPath, anchor: deepLink.anchor },
        };
      }
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(
          `[help-docs-panel] Deep-link path "${deepLink.docPath}" was not found in the docs manifest; falling back to ${FALLBACK_DOC_PATH}.`,
        );
      }
      return {
        docsOpen: true,
        docContext: { docPath: FALLBACK_DOC_PATH },
      };
    }
  }

  // Step 2 — fall back to persisted state.
  const persisted = readPersistedState(sessionKey);
  return {
    docsOpen: persisted?.open ?? false,
    docContext: { docPath: persisted?.lastDocPath },
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Return `true` when the currently-focused element is a text-editing
 * surface where the `?` key should type verbatim (never toggle the
 * slider).
 */
function isEditableTarget(target: EventTarget | null): boolean {
  if (target === null || !(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return false;
}

/**
 * Detail payload of the `help-docs-panel:open` CustomEvent — matches
 * the shape declared in `openDocsAt.ts`. Declared locally so the
 * provider does not depend on an internal export of that module.
 */
interface OpenDocsAtEventDetail {
  readonly path: string;
  readonly anchor?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Owner of the docs slider + chatbot state and portal roots.
 *
 * @see DocsSliderProviderProps
 */
export function DocsSliderProvider(
  props: DocsSliderProviderProps,
): JSX.Element {
  const { sessionKey, transport, currentRoute, manifest, children } = props;

  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------
  //
  // `docsOpen` and `docContext` are hydrated synchronously in a lazy
  // initialiser so the first paint already reflects the resolved state.
  // The resolver checks the deep-link (`?docs=…#anchor`) first — Req 1.7
  // and 1.8 give it precedence over persisted state — and falls back to
  // localStorage (Req 1.5) when no deep-link is present.
  //
  // `chatbotOpen` always starts `false` (Req 1.5: chatbot state is
  // never persisted).

  const initialState = useMemo(
    () => resolveInitialState(sessionKey, manifest),
    // The resolver reads `window.location` once per mount; the memo is
    // stable across renders as long as `sessionKey` and `manifest` do
    // not change. React's useState only consumes this value on the
    // first render, so re-computes on prop change are inert.
    [sessionKey, manifest],
  );

  const [docsOpen, setDocsOpen] = useState<boolean>(initialState.docsOpen);
  const [chatbotOpen, setChatbotOpen] = useState<boolean>(false);
  const [docContext, setDocContext] = useState<DocsSliderContext>(
    () => initialState.docContext,
  );

  // ------------------------------------------------------------------
  // Portal root — create (or reuse) `#help-docs-portal-root` in body
  // ------------------------------------------------------------------
  //
  // A ref rather than state is used so React does not attempt to render
  // the portal on the first render (before the effect runs). Instead we
  // hold the resolved element in state so the render pass after the
  // effect can materialise the portal children.

  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const createdPortalRootRef = useRef<boolean>(false);

  useEffect(() => {
    if (typeof document === "undefined") return;

    let root = document.getElementById(PORTAL_ROOT_ID);
    let created = false;
    if (root === null) {
      root = document.createElement("div");
      root.id = PORTAL_ROOT_ID;
      root.setAttribute(SELF_MOUNTED_ATTR, "true");
      document.body.appendChild(root);
      created = true;
    }
    createdPortalRootRef.current = created;
    setPortalRoot(root);

    return () => {
      if (createdPortalRootRef.current) {
        // Only tear down roots we created ourselves; a root supplied by
        // the app shell (via a wrapping layout) is owned by that shell.
        const selector = `#${PORTAL_ROOT_ID}[${SELF_MOUNTED_ATTR}="true"]`;
        const selfMounted = document.querySelector(selector);
        if (selfMounted !== null && selfMounted.parentNode !== null) {
          selfMounted.parentNode.removeChild(selfMounted);
        }
        createdPortalRootRef.current = false;
      }
      setPortalRoot(null);
    };
  }, []);

  // ------------------------------------------------------------------
  // Persistence — write on `docsOpen` / `docContext.docPath` change
  // ------------------------------------------------------------------
  //
  // We use a `hasHydratedRef` flag to skip the first effect firing so
  // the initial hydration read is not immediately overwritten by an
  // identical write. Chatbot state is never touched — the writer only
  // ever receives `{ open, lastDocPath, version }`.

  const hasHydratedRef = useRef<boolean>(false);
  useEffect(() => {
    if (!hasHydratedRef.current) {
      hasHydratedRef.current = true;
      return;
    }
    if (sessionKey === undefined) return;
    writePersistedState(sessionKey, {
      open: docsOpen,
      lastDocPath: docContext.docPath,
      version: 1,
    });
  }, [sessionKey, docsOpen, docContext.docPath]);

  // ------------------------------------------------------------------
  // Actions (stable identities — useCallback)
  // ------------------------------------------------------------------

  const openDocs = useCallback((path?: string, anchor?: string): void => {
    setDocsOpen(true);
    if (path !== undefined || anchor !== undefined) {
      setDocContext((prev) => ({
        ...prev,
        docPath: path ?? prev.docPath,
        anchor: anchor ?? prev.anchor,
      }));
    }
  }, []);

  const closeDocs = useCallback((): void => {
    setDocsOpen(false);
  }, []);

  const toggleDocs = useCallback((): void => {
    setDocsOpen((prev) => !prev);
  }, []);

  const openChatbot = useCallback((ctx?: Partial<DocsSliderContext>): void => {
    if (ctx !== undefined) {
      setDocContext((prev) => ({ ...prev, ...ctx }));
    }
    setChatbotOpen(true);
  }, []);

  const closeChatbot = useCallback((): void => {
    setChatbotOpen(false);
  }, []);

  const toggleChatbot = useCallback((): void => {
    setChatbotOpen((prev) => !prev);
  }, []);

  // ------------------------------------------------------------------
  // Window event bridge — `help-docs-panel:open`
  // ------------------------------------------------------------------

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = (event: Event): void => {
      // The event dispatched by `openDocsAt` is a CustomEvent; guard
      // defensively in case a synthetic Event with the same type is
      // dispatched by other code.
      const custom = event as CustomEvent<OpenDocsAtEventDetail>;
      const detail = custom.detail;
      if (
        detail === null ||
        detail === undefined ||
        typeof detail !== "object"
      ) {
        // Malformed event — still open the slider so the "?"-style
        // gesture works, but do not touch docContext.
        setDocsOpen(true);
        return;
      }
      const path = typeof detail.path === "string" ? detail.path : undefined;
      const anchor =
        typeof detail.anchor === "string" && ANCHOR_PATTERN.test(detail.anchor)
          ? detail.anchor
          : undefined;

      setDocsOpen(true);
      if (path !== undefined) {
        setDocContext((prev) => ({
          ...prev,
          docPath: path,
          anchor,
        }));
      }
    };

    window.addEventListener(OPEN_EVENT_TYPE, handler);
    return () => {
      window.removeEventListener(OPEN_EVENT_TYPE, handler);
    };
  }, []);

  // ------------------------------------------------------------------
  // Global keyboard shortcuts
  // ------------------------------------------------------------------

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = (event: KeyboardEvent): void => {
      // Never intercept keys while the user is typing in a text field.
      if (isEditableTarget(event.target)) return;

      // `?` (unmodified) — toggle slider.
      if (
        event.key === "?" &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        event.preventDefault();
        setDocsOpen((prev) => !prev);
        return;
      }

      // `Ctrl+/` or `⌘/` — toggle slider.
      if (event.key === "/" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        setDocsOpen((prev) => !prev);
        return;
      }

      // `Escape` — close slider when open.
      if (event.key === "Escape") {
        setDocsOpen((prev) => (prev ? false : prev));
        return;
      }
    };

    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, []);

  // ------------------------------------------------------------------
  // Context value (memoised for stable identity across renders)
  // ------------------------------------------------------------------

  const resolvedSessionId = sessionKey ?? "";

  const value = useMemo<DocsSliderContextValue>(
    () => ({
      docsOpen,
      chatbotOpen,
      docContext,
      transport,
      sessionId: resolvedSessionId,
      openDocs,
      closeDocs,
      toggleDocs,
      openChatbot,
      closeChatbot,
      toggleChatbot,
    }),
    [
      docsOpen,
      chatbotOpen,
      docContext,
      transport,
      resolvedSessionId,
      openDocs,
      closeDocs,
      toggleDocs,
      openChatbot,
      closeChatbot,
      toggleChatbot,
    ],
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  //
  // `DocsSliderContent` and `ChatbotDock` portal into `#help-docs-portal-root`.
  // `ChatbotFAB` renders inline — it manages its own portal into
  // `#chatbot-launcher-slot` via portalResolver.

  return (
    <DocsSliderContextObject.Provider value={value}>
      {children}
      {portalRoot !== null
        ? createPortal(
            <>
              <DocsSliderContent
                open={docsOpen}
                onClose={closeDocs}
                initialDocPath={docContext.docPath}
                initialAnchor={docContext.anchor}
                currentRoute={currentRoute}
                manifest={manifest}
              />
              <ChatbotDock
                open={chatbotOpen}
                onClose={closeChatbot}
                context={docContext}
                transport={transport}
                sessionId={resolvedSessionId}
              />
            </>,
            portalRoot,
          )
        : null}
      <ChatbotFAB onClick={openChatbot} />
    </DocsSliderContextObject.Provider>
  );
}
